/**
 * useRiseShatter — Fluid rise → mid-motion particle shatter notification
 * 
 * Spawns a colored text label (with optional emoji prefix) that rises from a 
 * target element, then shatters into individual character particles mid-flight.
 * Also spawns a glow ring pulse behind the target.
 * 
 * All animated elements are appended to document.body with fixed positioning
 * so they render above everything (video player, modals, etc.).
 * 
 * Usage:
 *   const triggerRiseShatter = useRiseShatter();
 *   triggerRiseShatter(buttonRef.current, '♥ Favorited', '#FC8181');
 */
import { useCallback } from 'react';

// Inject styles once
let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.id = 'rise-shatter-styles';
  style.textContent = `
    /* Rise-shatter: fluid rise phase — fixed to viewport */
    .rs-rise {
      position: fixed;
      pointer-events: none;
      white-space: nowrap;
      z-index: 999999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 12px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 4px;
      opacity: 0;
      transform: translateX(-50%);
      animation: rs-riseFluid 0.85s ease-out forwards;
    }
    @keyframes rs-riseFluid {
      0%   { opacity: 0; transform: translateX(-50%) translateY(6px); }
      12%  { opacity: 1; transform: translateX(-50%) translateY(-2px); }
      60%  { opacity: 1; transform: translateX(-50%) translateY(-28px); }
      85%  { opacity: 0.6; transform: translateX(-50%) translateY(-38px); }
      100% { opacity: 0; transform: translateX(-50%) translateY(-44px); }
    }

    /* Rise-shatter: glow ring pulse — rendered inline on the icon */
    .rs-glow-ring {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      pointer-events: none;
      transform: translate(-50%, -50%) scale(0.5);
      opacity: 0;
      z-index: -1;
      animation: rs-glowPulse 800ms ease-out forwards;
    }
    @keyframes rs-glowPulse {
      0%   { opacity: 0.8; transform: translate(-50%, -50%) scale(0.5); }
      50%  { opacity: 0.4; transform: translate(-50%, -50%) scale(1.8); }
      100% { opacity: 0;   transform: translate(-50%, -50%) scale(2.2); }
    }

    /* Rise-shatter: individual character particles — fixed to viewport */
    .rs-particle {
      position: fixed;
      pointer-events: none;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-weight: 600;
      font-size: 12px;
      opacity: 0;
      z-index: 999999;
      white-space: nowrap;
      animation: rs-shatterFly var(--rs-dur) ease-out forwards;
    }
    @keyframes rs-shatterFly {
      0%   { opacity: 1; transform: translate(0, 0) scale(1) rotate(0deg); }
      30%  { opacity: 0.9; transform: translate(var(--rs-dx1), var(--rs-dy1)) scale(0.95) rotate(var(--rs-rot1)); }
      70%  { opacity: 0.4; transform: translate(var(--rs-dx2), var(--rs-dy2)) scale(0.8) rotate(var(--rs-rot2)); }
      100% { opacity: 0;   transform: translate(var(--rs-dx3), var(--rs-dy3)) scale(0.5) rotate(var(--rs-rot3)); }
    }
  `;
  document.head.appendChild(style);
}

/**
 * @returns {Function} triggerRiseShatter(targetElement, text, color)
 *   - targetElement: the DOM node (e.g. the VStack button) to spawn from
 *   - text: string like "♥ Favorited" or "✕ Removed"
 *   - color: CSS color string like "#FC8181"
 */
export default function useRiseShatter() {
  return useCallback((targetEl, text, color) => {
    if (!targetEl) return;
    injectStyles();

    // Get the target's position in the viewport
    const rect = targetEl.getBoundingClientRect();
    // Skip hidden/offscreen elements (zero-size rect means element isn't visible)
    if (rect.width === 0 && rect.height === 0) return;
    const centerX = rect.left + rect.width / 2;
    const topY = rect.top;

    // ── Phase 0: Glow ring on the icon area ──
    const iconArea = targetEl.querySelector('[class*="chakra-box"]') || targetEl.firstElementChild;
    if (iconArea) {
      const origPos = window.getComputedStyle(iconArea).position;
      if (origPos === 'static') iconArea.style.position = 'relative';
      const ring = document.createElement('div');
      ring.className = 'rs-glow-ring';
      ring.style.background = `radial-gradient(circle, ${color}66 0%, transparent 70%)`;
      iconArea.appendChild(ring);
      setTimeout(() => ring.remove(), 900);
    }

    // ── Phase 1: Rising text (fixed to viewport, appended to body) ──
    const rise = document.createElement('div');
    rise.className = 'rs-rise';
    rise.style.color = color;
    rise.style.left = centerX + 'px';
    rise.style.top = topY + 'px';
    rise.textContent = text;
    document.body.appendChild(rise);

    // ── Phase 2: Shatter mid-motion ──
    const shatterDelay = 580;
    setTimeout(() => {
      const riseRect = rise.getBoundingClientRect();
      const rCenterX = riseRect.left + riseRect.width / 2;
      const rCenterY = riseRect.top + riseRect.height / 2;

      const chars = text.replace(/\s/g, '\u00A0').split('');
      const charWidth = chars.length > 0 ? riseRect.width / chars.length : 8;

      chars.forEach((ch, i) => {
        const p = document.createElement('div');
        p.className = 'rs-particle';
        p.textContent = ch;
        p.style.color = color;
        p.style.left = (rCenterX - riseRect.width / 2 + i * charWidth) + 'px';
        p.style.top = rCenterY + 'px';

        // Fan outward with upward bias (inheriting rise momentum)
        const fanSpread = 120;
        const fanStart = -120;
        const charAngle = fanStart + (i / (chars.length - 1 || 1)) * fanSpread;
        const angle = charAngle + (Math.random() * 16 - 8);
        const rad = angle * Math.PI / 180;
        const upBias = -12 - Math.random() * 10;

        const d1 = 10 + Math.random() * 6;
        const d2 = 22 + Math.random() * 12;
        const d3 = 35 + Math.random() * 18;

        p.style.setProperty('--rs-dx1', `${Math.cos(rad) * d1}px`);
        p.style.setProperty('--rs-dy1', `${Math.sin(rad) * d1 + upBias * 0.3}px`);
        p.style.setProperty('--rs-dx2', `${Math.cos(rad) * d2}px`);
        p.style.setProperty('--rs-dy2', `${Math.sin(rad) * d2 + upBias * 0.7}px`);
        p.style.setProperty('--rs-dx3', `${Math.cos(rad) * d3}px`);
        p.style.setProperty('--rs-dy3', `${Math.sin(rad) * d3 + upBias}px`);
        p.style.setProperty('--rs-rot1', `${Math.random() * 20 - 10}deg`);
        p.style.setProperty('--rs-rot2', `${Math.random() * 40 - 20}deg`);
        p.style.setProperty('--rs-rot3', `${Math.random() * 60 - 30}deg`);
        p.style.setProperty('--rs-dur', `${0.45 + Math.random() * 0.25}s`);

        document.body.appendChild(p);
        setTimeout(() => p.remove(), 900);
      });
    }, shatterDelay);

    // Cleanup rise element
    setTimeout(() => rise.remove(), 900);
  }, []);
}
